import { useMemo, useState } from 'react';
import { Icon } from '@sakhtar/design-system/react';
import {
  fromJalali,
  toJalali,
  jalaliMonthLength,
  systemClock,
  type BusinessDate,
} from '@sakhtar/calendar';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import type { TaskListResponse } from '../../lib/types/topbar.js';
import { NO_PROJECT_LABEL } from '../../lib/types/projects.js';

const WEEKDAYS = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];
const JALALI_MONTH_NAMES = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
];
// `useGrouping: false` — `viewJy` is a 4-digit Jalali year (e.g. ۱۴۰۵), and
// `toLocaleString('fa-IR')`'s default grouping would insert a thousands
// separator ("٬") into it, which reads as broken on a year.
const fa = (n: number) => n.toLocaleString('fa-IR', { useGrouping: false });

function todayGregorian(): BusinessDate {
  return systemClock.today();
}

/** Persian week starts Saturday. JS getDay() is Sunday=0 — remap by +1 mod 7. */
function persianWeekdayOf(greg: BusinessDate): number {
  const [y, m, d] = greg.split('-').map(Number);
  return (new Date(y!, m! - 1, d!).getDay() + 1) % 7;
}

/**
 * UI_REFERENCE_SOURCE: Sakhtar Topbar Popovers.dc.html §03 تقویم سریع.
 * Real Jalali month grid via `@sakhtar/calendar` (the sole permitted Jalali
 * boundary, ADR-006 §4) — not a client-side date library. Agenda is real
 * data too: tasks assigned to the current user, due on the selected day
 * (same GET .../projects/tasks?assignee=me endpoint MyTasksPopover uses).
 *
 * The board's agenda rows also show meetings ("جلسه استقرار") — no meeting/
 * calendar-event backend exists anywhere in this repo, so this agenda is
 * task due-dates only, not the board's fuller mixed agenda. Not fabricated.
 */
export function QuickCalendarPopover({ slug, open }: { slug: string; open: boolean }) {
  const base = tenantBasePath(slug);
  const todayG = todayGregorian();
  const todayJ = toJalali(todayG);

  const [viewJy, setViewJy] = useState(todayJ.jy);
  const [viewJm, setViewJm] = useState(todayJ.jm);
  const [selectedG, setSelectedG] = useState(todayG);

  const list = useApiGet<TaskListResponse>(
    open ? `${base}/api/v1/projects/tasks?assignee=me&limit=100` : null,
    [],
  );

  const cells = useMemo(() => {
    const leading = persianWeekdayOf(fromJalali({ jy: viewJy, jm: viewJm, jd: 1 }));
    const length = jalaliMonthLength(viewJy, viewJm);
    const out: { label: string; greg: BusinessDate | null }[] = [];
    for (let i = 0; i < leading; i += 1) out.push({ label: '', greg: null });
    for (let jd = 1; jd <= length; jd += 1) {
      out.push({ label: fa(jd), greg: fromJalali({ jy: viewJy, jm: viewJm, jd }) });
    }
    return out;
  }, [viewJy, viewJm]);

  function goMonth(delta: 1 | -1) {
    let jm = viewJm + delta;
    let jy = viewJy;
    if (jm > 12) {
      jm = 1;
      jy += 1;
    }
    if (jm < 1) {
      jm = 12;
      jy -= 1;
    }
    setViewJm(jm);
    setViewJy(jy);
  }

  const agenda =
    list.status === 'ready' ? list.data.tasks.filter((t) => t.dueDate === selectedG) : [];
  const selectedJ = toJalali(selectedG);
  const agendaTitle =
    selectedG === todayG
      ? `امروز — ${fa(selectedJ.jd)} ${JALALI_MONTH_NAMES[selectedJ.jm - 1]}`
      : `${fa(selectedJ.jd)} ${JALALI_MONTH_NAMES[selectedJ.jm - 1]}`;

  return (
    <div>
      <div
        style={{
          padding: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          borderBottom: '1px solid var(--sk-divider)',
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>
          {JALALI_MONTH_NAMES[viewJm - 1]} {fa(viewJy)}
        </div>
        <span
          style={{ fontSize: 11, color: 'var(--sk-brand-600)', cursor: 'pointer', fontWeight: 500 }}
          onClick={() => {
            setViewJy(todayJ.jy);
            setViewJm(todayJ.jm);
            setSelectedG(todayG);
          }}
        >
          امروز
        </span>
        <button
          className="sk-btn sk-btn--ghost sk-btn--icon sk-btn--sm"
          onClick={() => goMonth(-1)}
        >
          <Icon name="chevronRight" size="sm" />
        </button>
        <button className="sk-btn sk-btn--ghost sk-btn--icon sk-btn--sm" onClick={() => goMonth(1)}>
          <Icon name="chevronLeft" size="sm" />
        </button>
      </div>

      <div style={{ padding: '10px 14px 14px', borderBottom: '1px solid var(--sk-divider)' }}>
        <div className="sk-datepick__grid">
          {WEEKDAYS.map((w) => (
            <div key={w} className="sk-datepick__wd">
              {w}
            </div>
          ))}
          {cells.map((c, i) =>
            c.greg === null ? (
              <div key={i} />
            ) : (
              <button
                key={i}
                className={`sk-datepick__day${c.greg === todayG ? ' is-today' : ''}${c.greg === selectedG ? ' is-selected' : ''}`}
                onClick={() => setSelectedG(c.greg!)}
              >
                {c.label}
              </button>
            ),
          )}
        </div>
      </div>

      <div style={{ padding: '12px 14px 6px', fontSize: 12, color: 'var(--sk-fg-muted)' }}>
        {agendaTitle}
      </div>
      <div style={{ padding: '0 8px 8px', maxHeight: 220, overflowY: 'auto' }}>
        {agenda.map((t) => (
          <div
            key={t.ref}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              padding: 10,
              borderRadius: 10,
            }}
          >
            <span
              style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <span style={{ fontSize: 13, fontWeight: 500 }}>{t.title}</span>
              <span style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}>
                {t.projectRef ?? NO_PROJECT_LABEL}
              </span>
            </span>
          </div>
        ))}
        {list.status === 'ready' && agenda.length === 0 ? (
          <div
            style={{
              padding: '28px 16px',
              textAlign: 'center',
              fontSize: 12,
              color: 'var(--sk-fg-subtle)',
            }}
          >
            برای این روز موردی ثبت نشده است.
          </div>
        ) : null}
      </div>

      <div style={{ padding: 10, textAlign: 'center', borderTop: '1px solid var(--sk-divider)' }}>
        <a
          href={`${base}/projects/tasks`}
          style={{ fontSize: 12, fontWeight: 500, color: 'var(--sk-brand-600)' }}
        >
          مشاهده کارتابل من
        </a>
      </div>
    </div>
  );
}
